/** How much empty space sits either side of a page, as a function of the sidebar rather than the route.
 *  Pixel-denominated: unpinning the rail hands back exactly `RAIL_DOCKED - RAIL_COLLAPSED` = 184px, spent on gutters. A tier is a PAIR of gutters (one per pin state); on `standard` and `medium` they differ by half the reclaimed rail per side,
 *  so the content box is the same width in both pin states and toggling the pin never reflows. Pixels, not a percentage: a percentage gutter cannot express "equal to the sidebar".
 *  `dense` takes a small base AND a small increment, deliberately breaking that invariant, because `/hr` cannot afford gutter (a fixed 392px sidebar plus a ~1558px table).
 *  Nothing gets a zero gutter: an opt-out means less space, never none. `dense` membership is measured (a gutter decides whether the content overflows); `medium` membership is a shape judgment
 *  (full-bleed pages whose grid pane scales to fill the width), `standard` with a smaller base, not a second `dense`. */

/** `w-60` on the rail's layout spacer in `App.tsx`: the docked width. */
export const RAIL_DOCKED_PX = 240
/** `w-14`: the collapsed strip that stays behind when the rail is unpinned. */
export const RAIL_COLLAPSED_PX = 56
/** What unpinning hands back to each side of the page: 184 / 2. */
export const RECLAIMED_PER_SIDE_PX = (RAIL_DOCKED_PX - RAIL_COLLAPSED_PX) / 2

export type PageTier = 'standard' | 'medium' | 'dense'

/** Gutter per side, in px, at `lg` and above; below `lg` every page is `px-4` (see `pageGutterClass`).
 *  `standard.undocked - standard.docked === RECLAIMED_PER_SIDE_PX` is the content-width invariant; do not break that pair without re-reading the header. */
export const TIER_GUTTER_PX: Record<PageTier, { docked: number; undocked: number }> = {
  // 24px pinned is deliberately LESS than the old `lg:w-[92%]` gave at 1912px.
  standard: { docked: 24, undocked: 24 + RECLAIMED_PER_SIDE_PX },
  // `medium` is `standard` with a 12px-smaller base, so it keeps the same-width-in-both-pin-states property `dense` gives up.
  medium: { docked: 12, undocked: 12 + RECLAIMED_PER_SIDE_PX },
  // Pinned 12px ties `medium`; the tiers diverge only when unpinned (`medium` 104px, `dense` 28px). 12px is what unwraps the last `/hr` row (measured at 1425px of content).
  dense: { docked: 12, undocked: 12 + 16 },
}

/** Which tier a route belongs to. Unnamed routes are `standard`, so no page needs an entry. Matching is EXACT: `/organization` carries the HR list, which needs the width, while `/hr/:id` is an ordinary page. */
const DENSE_ROUTES = new Set(['/', '/organization'])

/** See the header's `medium` section for the shape test these four pass. */
const MEDIUM_ROUTES = new Set(['/leave', '/time', '/inventory', '/hot-desk'])

export function pageTier(pathname: string): PageTier {
  if (DENSE_ROUTES.has(pathname)) return 'dense'
  if (MEDIUM_ROUTES.has(pathname)) return 'medium'
  return 'standard'
}

/** The Tailwind classes for a page's horizontal padding, as whole literal strings out of a static map so Tailwind v4's scanner can see them (an interpolated `lg:px-[${n}px]` compiles to nothing).
 *  Below `lg` the answer is always `px-4`: a phone never docks the rail, and putting every varying value behind `lg:` keeps the pin from reaching small breakpoints. */
export function pageGutterClass(pathname: string, docked: boolean): string {
  const tier = pageTier(pathname)
  if (tier === 'dense') return docked ? 'px-4 lg:px-[12px]' : 'px-4 lg:px-[28px]'
  if (tier === 'medium') return docked ? 'px-4 lg:px-[12px]' : 'px-4 lg:px-[104px]'
  return docked ? 'px-4 lg:px-[24px]' : 'px-4 lg:px-[116px]'
}
